import { useApolloClient } from '@apollo/client';
import classNames from 'classnames/bind';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryMe } from 'gql/queries/me';
import Button from 'uikit/Button';
import CheckBox from 'uikit/CheckBox';
import { setGarSharing } from '../../../../../../api/courses.api-service';
import Select from '../../../../../../uikit/Select';
import FormikEffect from '../../../../../Shared/FormikEffect';

import styles from './GAR.module.scss';

const cx = classNames.bind(styles);

const GAR = ({ course, handleModalClose }) => {
  const client = useApolloClient();
  const { t } = useTranslation('', {
    keyPrefix: 'Course/Teacher/Cover/CourseShare/GAR',
  });

  const { isLoading, data } = useQueryMe();

  const [selectedOrganizationUAI, setSelectedOrganizationUAI] = useState();
  const [garEtabs, setGarEtabs] = useState([]);
  const [sharingData, setSharingData] = useState(course.sharing);

  // Extract from the user data:
  // garDIV: the existing divisions (classes) that the user can share the course to.
  // garGRO: the existing groups that the user can share the course to.
  // initialValues: the divisions and the groups that the user has selected to share the course with.
  useEffect(() => {
    const meEtabs = isLoading ? {} : data.me.metadata.etabs || {};
    const etabsArray = Object.values(meEtabs);

    setSelectedOrganizationUAI(etabsArray[0]?.garUAI);
    setGarEtabs(etabsArray);
  }, [course, data, isLoading]);

  const { garDIV, garGRO, initialValues } = useMemo(() => {
    const etabDivisions = isLoading
      ? []
      : data.me.metadata.etabs[selectedOrganizationUAI]?.garDIV || [];
    const etabGroups = isLoading
      ? []
      : data.me.metadata.etabs[selectedOrganizationUAI]?.garGRO || [];

    const courseDivisions =
      course.sharing?.[selectedOrganizationUAI]?.div || [];
    const courseGroups = course.sharing?.[selectedOrganizationUAI]?.gro || [];

    const divisions = Object.fromEntries([
      ...etabDivisions.map((div) => [div, courseDivisions.includes(div)]),
      ...courseDivisions.map((div) => [div, true]),
    ]);
    const groups = Object.fromEntries([
      ...etabGroups.map((gro) => [gro, courseGroups.includes(gro)]),
      ...courseGroups.map((gro) => [gro, true]),
    ]);
    return {
      garDIV: Object.keys(divisions).sort((a, b) => a.localeCompare(b)),
      garGRO: Object.keys(groups).sort((a, b) => a.localeCompare(b)),
      initialValues: {
        garUAI: selectedOrganizationUAI,
        div: divisions,
        gro: groups,
      },
    };
  }, [isLoading, data, course, selectedOrganizationUAI]);

  const handleFormChange = useCallback(
    (values) => {
      if (!selectedOrganizationUAI) {
        return;
      }

      const selectedDivisions = Object.entries(values.div)
        .filter(([, v]) => v)
        .map(([key]) => key);
      const selectedGroups = Object.entries(values.gro)
        .filter(([, v]) => v)
        .map(([key]) => key);

      if (selectedDivisions.length === 0 && selectedGroups.length === 0) {
        return setSharingData((prevSharingData) => {
          if (!prevSharingData?.[selectedOrganizationUAI]) {
            return prevSharingData;
          }

          const {
            [selectedOrganizationUAI]: emptyOrg,
            ...sharingDataWithoutEmptyOrg
          } = prevSharingData;
          return sharingDataWithoutEmptyOrg;
        });
      }
      setSharingData((prevSharingData) => ({
        ...prevSharingData,
        [selectedOrganizationUAI]: {
          div: Object.entries(values.div ?? {})
            .filter(([, v]) => v)
            .map(([key]) => key),
          gro: Object.entries(values.gro ?? {})
            .filter(([, v]) => v)
            .map(([key]) => key),
          uai: selectedOrganizationUAI,
        },
      }));
    },
    [selectedOrganizationUAI, setSharingData],
  );

  const handleSave = async () => {
    try {
      const sharingDataArray = Object.values(sharingData);

      await setGarSharing(course.id, Object.values(sharingDataArray));

      // As we use a REST query to update data coming from a GraphQL query,
      // we need to manually update the Apollo cache to reflect the changes.
      client.cache.modify({
        id: client.cache.identify({ __typename: 'Course', id: course.id }),
        fields: {
          sharing: () => sharingData,
        },
      });

      handleModalClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSave}
      >
        {({ isSubmitting }) => {
          return (
            <>
              <FormikEffect onChange={handleFormChange} />

              <Form>
                <Select
                  label={t('organization')}
                  name="garUAI"
                  onChange={(changeEvt) =>
                    setSelectedOrganizationUAI(changeEvt.target.value)
                  }
                >
                  <option disabled value="">
                    {t('select-course')}
                  </option>
                  {garEtabs.map((etab) => (
                    <option key={etab.garUAI} value={etab.garUAI}>
                      {etab.organization.name}
                    </option>
                  ))}
                </Select>

                <div className={cx('checkboxes')}>
                  {garDIV.length > 0 && (
                    <>
                      <h2 className={cx('subtitle')}>{t('divisions')}</h2>

                      {garDIV.map((div) => {
                        const [key, name] = div.split('##');
                        if (key === undefined || name === undefined)
                          return null;
                        return (
                          <CheckBox
                            className={cx('checkbox')}
                            key={div}
                            name={`div.${div}`}
                            label={`${name} (${key})`}
                          />
                        );
                      })}
                    </>
                  )}
                  {garGRO.length > 0 && (
                    <>
                      <h2 className={cx('subtitle')}>{t('groups')}</h2>
                      {garGRO.map((gro) => {
                        const [key, name] = gro.split('##');
                        if (key === undefined || name === undefined)
                          return null;
                        return (
                          <CheckBox
                            className={cx('checkbox')}
                            key={gro}
                            name={`gro.${gro}`}
                            label={`${name} (${key})`}
                          />
                        );
                      })}
                    </>
                  )}
                </div>
                <Button
                  className={cx('action')}
                  intent="primary"
                  isLoading={isSubmitting}
                  size="large"
                  type="submit"
                >
                  {t('share')}
                </Button>
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
};

GAR.propTypes = {
  course: PropTypes.object.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

export default GAR;
